import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqData } from '@/mocks/comics';
import { Link } from 'react-router-dom';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 韩国漫画网',
  description: '韩国漫画网是一个免费在线韩漫阅读平台，致力于为广大漫画爱好者提供最新最全的韩国漫画资源。',
  url: 'https://www.koreanmanhwa.cn/about',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩国漫画网',
  url: 'https://www.koreanmanhwa.cn/',
  description: '免费在线韩漫阅读平台',
  foundingDate: '2023',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'service@koreanmanhwa.cn',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const highlights = [
  {
    icon: 'ri-shield-check-line',
    title: '正版授权',
    desc: '与多家韩国漫画平台合作，确保内容正版合规，为读者提供优质阅读体验。',
  },
  {
    icon: 'ri-flashlight-line',
    title: '极速更新',
    desc: '同步韩国最新连载进度，热门漫画保持日更或周更节奏，追更不等待。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '多端适配',
    desc: '完美适配手机、平板和电脑，竖屏阅读模式专为移动端优化。',
  },
  {
    icon: 'ri-group-line',
    title: '社区互动',
    desc: '活跃的读者社区，评论、点赞、分享，与同好一起讨论精彩剧情。',
  },
];

const stats = [
  { num: '3000+', label: '漫画作品' },
  { num: '12', label: '内容分类' },
  { num: '500万+', label: '月活跃读者' },
  { num: '99.7%', label: '好评率' },
];

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9p0ibiftmtnfcfh0fcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩国漫画网"
        description="韩国漫画网是一个免费在线韩漫阅读平台，致力于为广大漫画爱好者提供最新最全的韩国漫画资源。了解我们的品牌故事、平台优势和联系方式。"
        keywords="关于我们,韩国漫画网,漫画平台介绍,联系方式,漫画资源"
        canonicalUrl="https://www.koreanmanhwa.cn/about"
        schema={{ ...aboutSchema, ...orgSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Header */}
          <section className="text-center py-10 md:py-14" aria-label="关于我们标题">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
              关于 <span className="text-primary-500">韩国漫画网</span>
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-sm md:text-base">
              我们是一个专注于<strong>韩国漫画</strong>的免费在线阅读平台，致力于为广大漫画爱好者提供最新、最全、最优质的<strong>韩漫资源</strong>。
              无论你是喜欢热血玄幻还是甜蜜恋爱，这里都有属于你的<strong>精彩漫画</strong>世界。
            </p>
          </section>

          {/* Stats */}
          <section className="mb-10 md:mb-14" aria-label="平台数据">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-6 bg-background-50 border border-background-200/60 rounded-xl">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-1">{stat.num}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="mb-10 md:mb-14" aria-label="品牌故事">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg text-primary-600">
                <i className="ri-book-open-line"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">
                <a href="#brand-story">品牌故事</a>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-5">
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">我们的初心</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  韩国漫画网创立于2023年，由一群热爱韩漫的年轻人共同打造。我们发现许多优秀的韩国漫画在国内缺乏便捷的阅读渠道，于是决定创建一个真正为读者着想的免费韩漫平台。从最初几十部作品的简单页面，发展到如今拥有数千部漫画的综合平台，我们始终秉持"让好漫画触手可及"的初心。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们致力于打造中文互联网上最好的韩国漫画阅读平台。不仅提供海量的漫画资源，更注重阅读体验的每一个细节——从高清画质到流畅翻页，从智能推荐到个性化书架。未来，我们还将推出更多创新功能，让漫画阅读成为一种享受。我们相信，好的故事值得被更多人看见。
                </p>
              </div>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-10 md:mb-14" aria-label="平台优势">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-lg text-accent-600">
                <i className="ri-medal-line"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">
                <a href="#platform-highlights">平台优势</a>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {highlights.map((h, i) => (
                <div key={i} className="bg-background-50 border border-background-200/60 rounded-xl p-5 text-center">
                  <span className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-3">
                    <i className={`${h.icon} text-xl text-primary-600`}></i>
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{h.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{h.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-10 md:mb-14" aria-label="常见问题">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-7 h-7 flex items-center justify-center bg-secondary-100 rounded-lg text-secondary-600">
                <i className="ri-question-answer-line"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">
                <a href="#about-faq">常见问题</a>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
              {faqData.map((faq, index) => (
                <article key={index} className="p-5 bg-background-50 border border-background-200/60 rounded-xl" itemScope itemType="https://schema.org/Question">
                  <h3 className="font-semibold text-foreground-900 mb-2 text-sm" itemProp="name">{faq.question}</h3>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <p className="text-sm text-foreground-600 leading-relaxed" itemProp="text">{faq.answer}</p>
                  </div>
                </article>
              ))}
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          </section>

          {/* Contact Form */}
          <section id="contact" className="mb-10 md:mb-14" aria-label="联系我们">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg text-primary-600">
                <i className="ri-mail-send-line"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">
                <a href="#contact">联系我们</a>
              </h2>
            </div>
            <div className="max-w-2xl mx-auto bg-background-50 border border-background-200/60 rounded-xl p-6 md:p-8">
              {formState === 'success' ? (
                <div className="text-center py-8">
                  <span className="w-16 h-16 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-4">
                    <i className="ri-check-line text-3xl text-accent-600"></i>
                  </span>
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-500 mb-4">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setFormState('idle')}
                    className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />

                  {formState === 'error' && formError && (
                    <div className="p-3 bg-primary-50 border border-primary-200 rounded-xl text-sm text-primary-700">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
                  >
                    {formState === 'loading' ? '提交中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* Contact Info */}
          <section className="mb-10 md:mb-14 text-center" aria-label="联系方式">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-5 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-mail-line"></i>
                </span>
                <strong>邮箱：</strong>service@koreanmanhwa.cn
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-time-line"></i>
                </span>
                <strong>工作时间：</strong>周一至周五 9:00-18:00
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-feedback-line"></i>
                </span>
                <strong>回复时效：</strong>24小时内回复
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}